import {StateCreator} from "zustand/vanilla";
import {
  CreateGoalRequest,
  CreateGoalResponse,
  Goal,
  GetGoalResponse,
  ListGoalsResponse,
  SetGoalArchivedRequest,
  SetGoalArchivedResponse,
  UpdateGoalRequest,
  UpdateGoalResponse,
} from "@devplaninc/codeup";
import {goalAPI, goalArchiveAPI, goalsAPI} from "@/app/apiPaths";
import {jr} from "@/hooks/queries/response-handler";

export type GoalInput = Pick<Goal, "title" | "description" | "ownerId" | "targetDate">

export type GoalsStoreState = {
  goals: Record<number, Record<string, Goal>>
  fetchGoals(companyId: number, includeArchived?: boolean): Promise<Goal[]>
  fetchGoal(companyId: number, goalId: string): Promise<Goal>
  createGoal(companyId: number, input: GoalInput): Promise<Goal>
  updateGoal(companyId: number, input: {goalId: string; values: GoalInput}): Promise<Goal>
  setGoalArchived(companyId: number, goalId: string, archived: boolean): Promise<Goal>
}

export const createGoalsSlice: StateCreator<
  GoalsStoreState,
  [],
  [],
  GoalsStoreState
> = (set) => ({
  goals: {},
  fetchGoals: async (companyId, includeArchived = false) => {
    const response = await fetch(`${goalsAPI(companyId)}?includeArchived=${includeArchived}`);
    const js = await jr(response);
    const result = ListGoalsResponse.fromJSON(js);
    set(s => ({
      ...s,
      goals: {
        ...s.goals,
        [companyId]: upsertGoals(s.goals[companyId], result.goals),
      },
    }));
    return result.goals;
  },
  fetchGoal: async (companyId, goalId) => {
    const response = await fetch(goalAPI(companyId, goalId));
    const js = await jr(response);
    const {goal} = GetGoalResponse.fromJSON(js);
    if (!goal) throw new Error("Goal response did not include a goal");
    setGoal(set, companyId, goal);
    return goal;
  },
  createGoal: async (companyId, input) => {
    const response = await fetch(goalsAPI(companyId), {
      method: "POST",
      body: JSON.stringify(CreateGoalRequest.toJSON(CreateGoalRequest.create({
        goal: Goal.create({
          workspaceId: String(companyId),
          ...input,
        }),
      }))),
    });
    const js = await jr(response);
    const {goal} = CreateGoalResponse.fromJSON(js);
    if (!goal) throw new Error("Goal creation response did not include a goal");
    setGoal(set, companyId, goal);
    return goal;
  },
  updateGoal: async (companyId, input) => {
    const response = await fetch(goalAPI(companyId, input.goalId), {
      method: "PATCH",
      body: JSON.stringify(UpdateGoalRequest.toJSON(UpdateGoalRequest.create({
        goal: Goal.create({
          id: input.goalId,
          workspaceId: String(companyId),
          ...input.values,
        }),
      }))),
    });
    const js = await jr(response);
    const {goal} = UpdateGoalResponse.fromJSON(js);
    if (!goal) throw new Error("Goal update response did not include a goal");
    setGoal(set, companyId, goal);
    return goal;
  },
  setGoalArchived: async (companyId, goalId, archived) => {
    const response = await fetch(goalArchiveAPI(companyId, goalId), {
      method: "PATCH",
      body: JSON.stringify(SetGoalArchivedRequest.toJSON(SetGoalArchivedRequest.create({
        workspaceId: String(companyId),
        goalId,
        archived,
      }))),
    });
    const js = await jr(response);
    const {goal} = SetGoalArchivedResponse.fromJSON(js);
    if (!goal) throw new Error("Goal archive response did not include a goal");
    setGoal(set, companyId, goal);
    return goal;
  },
});

function upsertGoals(existing: Record<string, Goal> | undefined, goals: Goal[]): Record<string, Goal> {
  const next = {...(existing || {})};
  for (const goal of goals) {
    next[goal.id] = goal;
  }
  return next;
}

function setGoal(
  set: Parameters<StateCreator<GoalsStoreState>>[0],
  companyId: number,
  goal: Goal,
) {
  set(s => ({
    ...s,
    goals: {
      ...s.goals,
      [companyId]: {
        ...(s.goals[companyId] || {}),
        [goal.id]: goal,
      },
    },
  }));
}
